// src/apiServices/authService.js

import axios from 'axios';
import { APIUserLogin, APIUserLogout, BASE_URL } from './APIs';

// Storage keys
const USER_INFO_KEY = 'userInfo';
const USER_PERMISSIONS_KEY = 'userPermissions';

class AuthService {
    constructor() {
        this.api = axios.create({
            baseURL: BASE_URL,
            timeout: 30000,
            headers: {
                'Accept': 'application/json',
                "Content-Type": "multipart/form-data",
            }
        });
    }

    /**
     * Login user
     */
 async login(email, password) {
        try {
            const response = await this.api.post(APIUserLogin, {
                email,
                password
            });

            if (response.data.status === 'success') {
                const { data } = response.data;
                
                // Store tokens
                // Store user info
                sessionStorage.setItem(USER_INFO_KEY, JSON.stringify(data));
                sessionStorage.setItem(USER_PERMISSIONS_KEY,data.permissions);
                
                // Start auto refresh timer
                // this.startAutoRefreshTimer();

                return {
                    success: true,
                    data: data
                };
            } else {
                return {
                    success: false,
                    message: response.data.message
                };
            }
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed'
            };
        }
    }

    /**
     * Logout user
     */
    async logout() {
        try {
            await this.api.post(APIUserLogout);
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            this.clearAllData();
        }
    }

    /**
     * Clear all stored user data
     */
    clearAllData() {
        sessionStorage.removeItem(USER_INFO_KEY);
        sessionStorage.removeItem(USER_PERMISSIONS_KEY);
        sessionStorage.removeItem('userInfo');
        sessionStorage.removeItem('userPermissions');
    }

    /**
     * Get user ID
     */
    getUserId() {
        const user = this.getCurrentUser();
        return user ? user.id : null;
    }

    /**
     * Get user name
     */
    getUserName() {
        const user = this.getCurrentUser();
        return user ? user.name : null;
    }

    /**
     * Get user email
     */
    getUserEmail() {
        const user = this.getCurrentUser();
        return user ? user.email : null;
    }

    /**
     * Get current user from storage
     */
    getCurrentUser() {
        const userInfo = sessionStorage.getItem(USER_INFO_KEY);
        try {
            return userInfo ? JSON.parse(userInfo) : null;
        } catch (error) {
            console.error('Error parsing user info:', error);
            return null;
        }
    }

    /**
     * Get user permissions
     */
    getUserPermissions() {
        const permissions = sessionStorage.getItem(USER_PERMISSIONS_KEY);
        try {
            return permissions ? JSON.parse(permissions) : ['VIEW'];
        } catch (e) {
            // Fallback in case it was stored as a raw string or array representation
            return permissions ? permissions.split(',') : ['VIEW'];
        }
    }

    /**
     * Check if user has specific permission
     */
    hasPermission(permission) {
        const permissions = this.getUserPermissions();
        return Array.isArray(permissions) && (permissions.includes(permission) || permissions.includes('ALL'));
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        const user = this.getCurrentUser();
        return !!user;
    }

    /**
     * Get axios instance
     */
    getApi() {
        return this.api;
    }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;